import { Alert } from "@msuite/ui/alert";
import { useTranslation } from "@msuite/ui/localization";
import type { IncomingInvoiceDetail } from "@msuite/utils/types";
import { useNavigate } from "react-router";
import { IncomingInvoiceDuplicateActions } from "@/components/incoming-invoices";
import { useAuth } from "@/hooks/use-auth";
import { useCitadel } from "@/hooks/use-citadel";

/** Props Interface */
interface IncomingInvoiceDuplicateNoticeProps {
	incomingInvoice: IncomingInvoiceDetail;
}

/**
 * What a duplicate asks of the people who review it, above its checks: it repeats an earlier
 * invoice, and is to be merged into it or declared none. The decision is offered to the invoice's
 * approvers and to anyone who may edit invoices — the same people the server lets decide.
 */
export function IncomingInvoiceDuplicateNotice({
	incomingInvoice,
}: IncomingInvoiceDuplicateNoticeProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const navigate = useNavigate();
	const { user } = useAuth();
	const { citadel } = useCitadel();

	/** Computed */
	const original = incomingInvoice.duplicateOf;
	const isApprover = incomingInvoice.approvals.some((approval) => approval.user.id === user?.id);
	const mayDecide = isApprover || citadel?.can("update", "IncomingInvoice") === true;

	/** Guard */
	if (incomingInvoice.state !== "duplicate" || !original) return null;

	/** Render */
	return (
		<div className="v-stack gap-3">
			<Alert
				variant="danger"
				title={t("incoming_invoices.duplicate_title", { number: original.number ?? "–" })}
				description={t("incoming_invoices.duplicate_description")}
				onClick={() => navigate(`/controlling/incoming-invoices/${original.id}`)}
			/>
			{mayDecide && (
				<div className="h-stack gap-2 justify-end">
					<IncomingInvoiceDuplicateActions
						incomingInvoiceId={incomingInvoice.id}
						onMerged={() => navigate(`/controlling/incoming-invoices/${original.id}`)}
					/>
				</div>
			)}
		</div>
	);
}
